<script setup lang="ts">
import {PropType} from "vue";

defineProps({
  loading: {
    type: Boolean as PropType<boolean>,
    default: false
  },
  icon: {
    type: String as PropType<string>,
  },
  color: {
    type: String as PropType<string>,
    default: "primary"
  },
  iconBackgroundColor: {
    type: String as PropType<string>,
    default: "bg-primary-lighten-4"
  },
  tileBackgroundColor: {
    type: String as PropType<string>,
    default: "bg-primary-lighten-5"
  },
  metricTextColor: {
    type: String as PropType<string>,
    default: "text-primary"
  },
  progressBarColor: {
    type: String as PropType<string>,
    default: "primary"
  },
  noData: {
    type: Boolean as PropType<boolean>,
    default: false
  },
  progressBarValue: {
    type: Number as PropType<number | null>,
    default: null
  },
});
</script>

<template>
  <v-card
    :loading="loading"
    min-height="200px"
    height="100%"
    variant="flat"
    class="mini-metric-tile"
    :class="`bg-${color}-lighten-4`"
    :disabled="noData"
  >
    <v-card-text class="d-flex flex-column align-center justify-center h-100 pa-4">
      <!-- Icon -->
      <div class="mb-3">
        <v-icon :color="color" size="large">
          <slot name="icon">{{ icon }}</slot>
        </v-icon>
      </div>

      <!-- Title -->
      <div class="text-center mb-3">
        <span class="text-body-2 font-weight-bold text-grey-darken-2">
          <slot name="title"/>
        </span>
      </div>

      <!-- Metric -->
      <div class="text-center" v-if="!noData">
        <span class="font-weight-bold" :class="`text-${color}`" style="font-size: 36px; line-height: 1;">
          <slot name="metric"/>
        </span>
        <span class="font-weight-bold" :class="`text-${color}`" style="font-size: 24px;" v-if="$slots.overMetric">
          / <slot name="overMetric"/>
        </span>
      </div>

      <!-- No Data State -->
      <div class="text-center" v-else>
        <span class="font-weight-bold text-grey" style="font-size: 36px; line-height: 1;">0</span>
      </div>

      <!-- Progress Bar -->
      <div class="w-100 mt-3" v-if="progressBarValue !== null">
        <v-progress-linear
          :model-value="progressBarValue"
          height="8"
          :color="color"
          rounded
        />
      </div>

      <!-- Comment/Additional Info -->
      <div class="text-center mt-2" v-if="$slots.comment">
        <span class="text-caption">
          <slot name="comment"/>
        </span>
      </div>
    </v-card-text>
  </v-card>
</template>

<style scoped>
.mini-metric-tile {
  border: none;
  border-radius: 12px;
  transition: all 0.2s ease;
}

.mini-metric-tile:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}
</style>
